import { FormikProps } from 'formik'
import { useEffect, useMemo } from 'react'
import { POSOrderDetails } from '.'
import { Button } from '../../components/button'
import { EmptyState } from '../../components/emptyState/emptyState'
import { TextInput } from '../../components/inputs/textInput'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { POSCustomerDetails } from '../../components/modal/app_modals/POSCustomerDetailsModal'
import { POSFulfilmentTypeModal } from '../../components/modal/app_modals/POSFulfilmentTypeModal'
import { POSOrderReceiptModal } from '../../components/modal/app_modals/POSOrderReceiptModal'
import { POSPaymentMethod } from '../../components/modal/app_modals/POSPaymentMethodModal'
import { LoadingOval } from '../../components/spinner/Spinner'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import { PRIMARY_COLOR } from '../../constants'
import { areArraysSame, formatCurrency } from '../../helpers'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import {
  updateAdminCartOrder,
  updateCurrentCartId,
} from '../../redux-toolkits/auth/auth.reducer'
import {
  useCreateCartMutation,
  useGetCartQuery,
  useRemoveItemFromCartMutation,
  useUpdateCartItemQuantityMutation,
} from '../../redux-toolkits/orders/orders.slice'
import { CartSummaryItem } from './cartSummaryItem'

export const CartSummary = ({
  className,
  addingItemToCart,
  formikVar,
  refetchProducts,
}: {
  className: string
  addingItemToCart: boolean
  formikVar: FormikProps<POSOrderDetails>
  refetchProducts: () => void
}) => {
  const { currentCart, adminCart, adminCartOrder, adminCartItems } =
    useAppSelector((state) => state.auth)

  const dispatch = useAppDispatch()

  const cartAmountFees = useMemo(() => {
    return [
      {
        title: 'Subtotal',
        value: formatCurrency(
          (adminCart?.totalAmount ?? 0) - (adminCart?.totalServiceCharge ?? 0),
        ),
      },
      {
        title: 'Service fee',
        value: formatCurrency(adminCart?.totalServiceCharge ?? 0),
      },
      {
        title: 'Total',
        value: formatCurrency(adminCart?.totalAmount ?? 0),
      },
    ]
  }, [adminCart])

  const { handleModalClose, handleModalOpen, setModalOpen, modal, modalOpen } =
    useModalControl()

  const { isFetching: loadingCartDetails } = useGetCartQuery(currentCart!, {
    refetchOnMountOrArgChange: true,
    skip: !currentCart,
  })

  const [updateCartItemQuantity, { isLoading: updatingCartItemQuantity }] =
    useUpdateCartItemQuantityMutation()

  const [removeItemFromCart, { isLoading: removingItemFromCart }] =
    useRemoveItemFromCartMutation()

  const [createCart, { isLoading: creatingCart }] = useCreateCartMutation()

  useEffect(() => {
    // Create cart on load if no cart exists
    if (!currentCart && !creatingCart) {
      createCart()?.then((resp) => {
        if (resp.data?.success) {
          dispatch(updateCurrentCartId(resp.data.data.cart._id))
        }
      })
    }
  }, [currentCart])

  return (
    <>
      <div className={className}>
        <HeaderSubheader title="Cart Summary" />

        {/* Loading state  */}
        {loadingCartDetails || addingItemToCart || creatingCart ? (
          <div className="flex w-full items-center justify-center mt-10">
            <LoadingOval color={PRIMARY_COLOR} />
          </div>
        ) : undefined}

        {/* Empty state  */}
        {adminCartItems?.length === 0 &&
        !loadingCartDetails &&
        !addingItemToCart &&
        !creatingCart ? (
          <EmptyState image="/assets/images/empty-cart.png" text="Empty Cart" />
        ) : undefined}

        {/* Filled state  */}
        {(adminCartItems?.length ?? 0) > 0 &&
        !loadingCartDetails &&
        !addingItemToCart &&
        !creatingCart ? (
          <div className="flex mt-8 flex-col">
            {(adminCartItems ?? []).map((cartItem, idx) => {
              const matchingProductVariantCombination =
                cartItem.product.variantCombinations.find((el) =>
                  areArraysSame(
                    el.variantCombination.map((i) => i.value),
                    cartItem.variants?.map((i) => i.value) ?? [],
                  ),
                )

              return (
                <CartSummaryItem
                  key={`cartItem-${idx}`}
                  cartItem={cartItem}
                  matchingProductVariantCombination={
                    matchingProductVariantCombination
                  }
                />
              )
            })}

            <div className="mt-6 flex flex-col gap-6">
              {adminCartOrder ? undefined : (
                <div className="pb-6 border-b">
                  <TextInput
                    type="text"
                    name="orderNote"
                    hasIcon={false}
                    value={formikVar.values.orderNote}
                    placeholder="Note (Optional)"
                    onChange={formikVar.handleChange}
                    onBlur={formikVar.handleBlur}
                    errors={
                      formikVar.touched.orderNote && formikVar.errors.orderNote
                        ? formikVar.errors.orderNote
                        : ''
                    }
                  />
                </div>
              )}

              <div className="flex flex-col gap-2">
                {cartAmountFees.map((fee, idx) => {
                  return (
                    <p
                      key={`${idx}`}
                      className="flex items-center justify-between gap-2 text-sm text-sec-black"
                    >
                      <span>{fee.title}</span>
                      <span>{fee.value}</span>
                    </p>
                  )
                })}
              </div>

              <Button
                type="button"
                label={adminCartOrder ? 'Pay' : 'Continue'}
                onClick={() => {
                  if (adminCartOrder) {
                    handleModalOpen('paymentMethodModal')
                  } else {
                    handleModalOpen('customerDetailsModal')
                  }
                }}
                primary
                className="w-full"
                disabled={
                  loadingCartDetails ||
                  removingItemFromCart ||
                  updatingCartItemQuantity ||
                  !formikVar.values.fulfilmentCenter
                }
              />

              {adminCartOrder ? (
                <Button
                  type="button"
                  label="Clear"
                  onClick={() => {
                    handleModalOpen('confirmClearDetails')
                  }}
                  variant="plain"
                />
              ) : undefined}
            </div>
          </div>
        ) : undefined}
      </div>

      <POSCustomerDetails
        open={modal === 'customerDetailsModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalOpen('fulfilmentTypeModal')
        }}
        formik={formikVar}
      />

      <POSFulfilmentTypeModal
        open={modal === 'fulfilmentTypeModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={async (order, isOpenOrder) => {
          // Update state with created order to persist data
          dispatch(updateAdminCartOrder(order))
          if (isOpenOrder) {
            handleModalClose()
          } else {
            handleModalOpen('paymentMethodModal')
          }
        }}
        formik={formikVar}
      />

      {adminCartOrder ? (
        <POSPaymentMethod
          open={modal === 'paymentMethodModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            handleModalClose()
          }}
          onSuccessCallback={() => {
            formikVar.resetForm({
              values: {
                ...formikVar.initialValues,
                fulfilmentCenter: formikVar.values.fulfilmentCenter,
              },
            })
            refetchProducts()
            // Change curentCart to undefined to trigger new cart creation and update other cart states
            dispatch(updateCurrentCartId(undefined))
            // Open success receipt modal
            handleModalOpen('POSOrderReceiptModal')
          }}
          formik={formikVar}
          order={adminCartOrder}
        />
      ) : undefined}

      {adminCartOrder && modal === 'POSOrderReceiptModal' && modalOpen ? (
        <POSOrderReceiptModal
          open={modal === 'POSOrderReceiptModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            // Clear order in state
            dispatch(updateAdminCartOrder(undefined))
            handleModalClose()
          }}
          harmonizedReference={adminCartOrder.harmonizedReference}
        />
      ) : undefined}

      <ConfirmationModal
        open={modalOpen && modal === 'confirmClearDetails'}
        setOpen={setModalOpen}
        message="Are you sure you want to clear the cart and related details? You can return to it later if the order is open."
        title="Clear Details?"
        onCloseCallback={() => {
          handleModalClose()
        }}
        onConfirm={() => {
          dispatch(updateCurrentCartId(undefined))
          dispatch(updateAdminCartOrder(undefined))
          formikVar.resetForm({
            values: {
              ...formikVar.initialValues,
              fulfilmentCenter: formikVar.values.fulfilmentCenter,
            },
          })
          handleModalClose()
        }}
        confirmText="Clear"
      />
    </>
  )
}
